import { ParamValidator, VF } from '@rd-web-markets/shared/dist/models/ParamValidator';
import { useState, useCallback } from 'react';

/**
 * Manage row expansion state and logic for DataGrid.
 * When expanding rows - their ids are stored in this hook's state of expanded row ids.
 * When collapsing rows - their ids are removed from this hook's state of expanded row ids.
 * When rows are edited / sorted / filtered - this hook sorts and filters child rows before inserting them after their parent row.
 * 
 * @param {boolean} enabled - Whether row expansion is enabled
 * @param {Function} onRowExpand - Callback to fetch child rows (can return fresh data on each call)
 * @param {Function} rowKeyGetter - Function to get unique key for each row
 * @param {string} expandedRowProp - Property name to track expansion state
 * @param {string} childRowProp - Property name to mark child rows
 * @param {Function} getChildRows - Optional function to get fresh child rows for a parent (for reactive updates)
 * @param {Function} sortChildRows - Optional function to sort child rows (for consistent sorting)
 * @returns {Object} { toggleRowExpansion, insertChildRows }
 */
export function useDataGridRowExpansion(enabled, onRowExpand, rowKeyGetter, expandedRowProp, childRowProp) {
  let getChildRows = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : null;
  let sortChildRows = arguments.length > 6 ? arguments[6] : undefined;
  let applyFilters = arguments.length > 7 ? arguments[7] : undefined;
  ParamValidator.validate('DataGridTable - useDataGridRowExpansion', [[enabled, VF.isBoolean], [onRowExpand, VF.isNonRequiredFunction], [rowKeyGetter, VF.isFunction], [expandedRowProp, VF.isNonEmptyString], [childRowProp, VF.isNonEmptyString], [getChildRows, VF.isNonRequiredFunction], [sortChildRows, VF.isFunction], [applyFilters, VF.isFunction]]);

  // Store expanded parent row IDs instead of caching children
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const toggleRowExpansion = useCallback(async row => {
    if (!enabled || !onRowExpand) return;
    const rowId = rowKeyGetter(row);
    const isExpanded = row[expandedRowProp];
    if (isExpanded) {
      setExpandedRowIds(prev => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      row[expandedRowProp] = false;
    } else {
      // Expanding - fetch child rows (or they'll be looked up later if getChildRows is provided)
      await onRowExpand(row);
      row[expandedRowProp] = true;
      setExpandedRowIds(prev => new Set([...prev, rowId]));
    }
  }, [enabled, onRowExpand, rowKeyGetter, expandedRowProp]);
  const insertChildRows = useCallback(rows => {
    if (!enabled || expandedRowIds.size === 0) return rows;
    const resultWithChildren = [];
    rows.forEach(row => {
      resultWithChildren.push(row);

      // Check if this row is expanded
      const rowId = rowKeyGetter(row);
      if (expandedRowIds.has(rowId)) {
        // Get fresh child rows if getChildRows function is provided
        if (getChildRows) {
          let childRows = getChildRows(row);
          if (childRows && childRows.length > 0) {
            childRows.forEach(childRow => {
              childRow[childRowProp] = true;
            });
            childRows = sortChildRows(childRows);
            childRows = applyFilters(childRows);
            resultWithChildren.push(...childRows);
          }
        }
      }
    });
    return resultWithChildren;
  }, [enabled, expandedRowIds, rowKeyGetter, getChildRows, sortChildRows, applyFilters, childRowProp]);
  return {
    toggleRowExpansion,
    insertChildRows
  };
}